import { t } from "@lingui/core/macro";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { machineFormat } from "@medoma/models/date-and-time";
import { useQuery } from "@tanstack/react-query";
import type { z } from "zod";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import {
  fetchPatients,
  resolvePatient,
  type IListExistingPatient,
} from "./Patients";
import { type IVisit } from "@medoma/models/visits";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { i18n } from "@lingui/core";
import { patientStatusSchema } from "@medoma/models/patients";
import type { routeSchema, routesSchema } from "@medoma/models/routes";
import { type IRouteStatus } from "@medoma/models/routes";
import { unenrichedRoutesSchema } from "@medoma/models/routes.unenriched";

export const timeOfDay = (time: "Any" | Date) => {
  if (time === timeOfDaySchema.enum.Any) {
    return i18n._(timeOfDayDictionary.Any.short);
  }
  const hour = time.getHours();

  if (hour >= 4 && hour < 10) {
    return t`Morgon`;
  }

  if (hour >= 10 && hour < 12) {
    return t`Förmiddag`;
  }

  if (hour >= 12 && hour < 17) {
    return t`Eftermiddag`;
  }

  if (hour >= 17 && hour < 22) {
    return t`Kväll`;
  }

  if (hour >= 22 || (hour >= 0 && hour < 4)) {
    return t`Natt`;
  }

  return t`[tid på dagen]`;
};

const generateRouteName = (route: z.infer<typeof routeSchema>) => {
  const { visits } = route;
  const occurrences = visits.flatMap((visit) => visit.occurrences);
  if (visits.length === 0) {
    return t`Ny rutt`;
  }
  if (!occurrences[0]) {
    return t`Ny rutt`;
  }
  const specificTimeOccurrences = occurrences.filter(
    (occurrence) => occurrence.timeOfDay === "Specific",
  );
  if (!specificTimeOccurrences[0]) {
    return i18n._(timeOfDayDictionary.Any.short);
  }
  return timeOfDay(
    specificTimeOccurrences[0].timeOfDay === timeOfDaySchema.enum.Any
      ? timeOfDaySchema.enum.Any
      : specificTimeOccurrences[0].start,
  );
};

const nameRoutes = (routes: z.infer<typeof routesSchema>) => {
  return routes.map((route) => nameRoute(route));
};

const nameRoute = (route: z.infer<typeof routeSchema>) => {
  const name = generateRouteName(route);
  return {
    ...route,
    name,
  };
};

const patientIdsInRoutes = (
  unenrichedRoutes: z.infer<typeof unenrichedRoutesSchema>,
) =>
  deduplicatePrimitiveArray(
    unenrichedRoutes
      .flatMap(({ visits }) => visits)
      .flatMap(({ patientId }) => patientId),
  );

export const parseAndEnrichRoutes = async (probableRoutes: unknown) => {
  const possiblyNamelessUnenrichedRoutes =
    unenrichedRoutesSchema.parse(probableRoutes);

  if (possiblyNamelessUnenrichedRoutes.length === 0) {
    return possiblyNamelessUnenrichedRoutes as [];
  }

  const patientIds = patientIdsInRoutes(possiblyNamelessUnenrichedRoutes);
  const patients = await fetchPatients({ patientIds });
  // The visits are already decoded; enrichment swaps patientId for the patient.
  const possiblyNamelessRoutes = possiblyNamelessUnenrichedRoutes.map(
    (route) => ({
      ...route,
      visits: route.visits.map(({ patientId, ...visit }) => ({
        ...visit,
        patient: resolvePatient({ patientId, patients }),
      })),
    }),
  );
  const namedRoutes = nameRoutes(possiblyNamelessRoutes);
  return namedRoutes;
};
async function fetchRoutes({
  from,
  to,
  status,
}: {
  from: string;
  to: string;
  status?: IRouteStatus;
}) {
  const queryString = generateQueryString({
    from,
    to,
    status,
  });
  const parsedRoutes = await parseAndEnrichRoutes(
    await logisticsApi.get(`/routes${queryString}`).json(),
  );
  return parsedRoutes;
}

export const routeKeys = {
  all: ["routes"] as const,
  lists: () => [...routeKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...routeKeys.lists(), { filters }] as const,
  detail: (id: string) => [...routeKeys.all, id, "details"] as const,
};

export const useRoutes = ({
  from,
  to,
  status,
}: {
  from: Date;
  to: Date;
  status?: IRouteStatus;
}) => {
  const formattedFrom = machineFormat(from, "yyyy-MM-dd");
  const formattedTo = machineFormat(to, "yyyy-MM-dd");
  return useQuery({
    queryKey: routeKeys.list({ from: formattedFrom, to: formattedTo, status }),
    queryFn: () =>
      fetchRoutes({ from: formattedFrom, to: formattedTo, status }),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const getVisitsCoordinates = (visits: IVisit[]) => {
  return (
    visits.filter(
      ({ patient: { status } }) => status !== patientStatusSchema.enum.deleted,
    ) as ReadonlyArray<{
      patient: IListExistingPatient;
    }>
  ).map(({ patient }) => {
    const {
      address: { coordinates },
    } = patient;

    return {
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
    };
  });
};

export async function unassignGroupFromRoute(groupId: string, routeId: string) {
  await logisticsApi.post(`/routes/${routeId}/unassign`, {
    json: { occurrenceGroupId: groupId },
  });
}

export async function moveGroupInRoute(
  groupId: string,
  routeId: string,
  desiredPositionInRoute: number,
) {
  await logisticsApi.post(`/routes/${routeId}/sort`, {
    json: { groupId, routeIndex: desiredPositionInRoute },
  });
}
